import React from 'react';
import { Timeline, Pagination } from 'antd';
import {
  PlusCircleOutlined, CaretRightOutlined, PauseCircleOutlined,
  PlayCircleOutlined, StopOutlined, MessageOutlined,
  TrophyOutlined, ClockCircleOutlined, HistoryOutlined,
} from '@ant-design/icons';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const ACTION_CONFIG = {
  created: {
    icon: <PlusCircleOutlined style={{ fontSize: 14 }} />,
    color: COLORS.blue,
    bg: COLORS.blueBg,
    label: 'Created',
  },
  started: {
    icon: <PlayCircleOutlined style={{ fontSize: 14 }} />,
    color: COLORS.emerald,
    bg: COLORS.emeraldBg,
    label: 'Started',
  },
  paused: {
    icon: <PauseCircleOutlined style={{ fontSize: 14 }} />,
    color: COLORS.amber,
    bg: COLORS.amberBg,
    label: 'Paused',
  },
  resumed: {
    icon: <PlayCircleOutlined style={{ fontSize: 14 }} />,
    color: COLORS.emerald,
    bg: COLORS.emeraldBg,
    label: 'Resumed',
  },
  cancelled: {
    icon: <StopOutlined style={{ fontSize: 14 }} />,
    color: COLORS.rose,
    bg: COLORS.roseBg,
    label: 'Cancelled',
  },
  reply: {
    icon: <MessageOutlined style={{ fontSize: 14 }} />,
    color: COLORS.violet,
    bg: COLORS.violetBg,
    label: 'Reply Received',
  },
  winner: {
    icon: <TrophyOutlined style={{ fontSize: 14 }} />,
    color: COLORS.amber,
    bg: COLORS.amberBg,
    label: 'Winner Selected',
  },
};

const DEFAULT_CONFIG = {
  icon: <ClockCircleOutlined style={{ fontSize: 14 }} />,
  color: COLORS.textMuted,
  bg: 'rgba(255,255,255,0.06)',
  label: 'Activity',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ActivityTimeline = ({ log = [], total = 0, onPageChange }) => {
  if (log.length === 0) {
    return (
      <div style={{ ...glassCard, padding: 32, textAlign: 'center' }}>
        <HistoryOutlined style={{ fontSize: 28, color: COLORS.textMuted, marginBottom: 8 }} />
        <div style={{ color: COLORS.textSecondary, fontSize: 13 }}>
          No activity recorded yet
        </div>
      </div>
    );
  }

  const timelineItems = log.map((entry, idx) => {
    const config = ACTION_CONFIG[entry.action] || DEFAULT_CONFIG;
    return {
      key: entry._id || idx,
      dot: (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 30,
          borderRadius: 8,
          background: config.bg,
          color: config.color,
        }}>
          {config.icon}
        </div>
      ),
      children: (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '4px 0 12px 8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: config.color,
              textTransform: 'capitalize',
            }}>
              {config.label}
            </span>
            {entry.performer && (
              <span style={{
                fontSize: 11,
                color: COLORS.textSecondary,
                fontWeight: 500,
              }}>
                by {entry.performer}
              </span>
            )}
            <span style={{
              fontSize: 10,
              color: COLORS.textMuted,
              marginLeft: 'auto',
            }}>
              {formatTimestamp(entry.timestamp || entry.createdAt)}
            </span>
          </div>
          {entry.details && (
            <span style={{
              fontSize: 12,
              color: COLORS.textSecondary,
              lineHeight: 1.4,
            }}>
              {entry.details}
            </span>
          )}
        </div>
      ),
    };
  });

  return (
    <div style={{ ...glassCard, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <HistoryOutlined style={{ color: COLORS.violet, fontSize: 16 }} />
        <span style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Activity Log</span>
        <span style={{
          fontSize: 11,
          color: COLORS.textMuted,
          padding: '1px 8px',
          borderRadius: 5,
          background: 'rgba(255,255,255,0.06)',
        }}>
          {total} total
        </span>
      </div>

      {/* Timeline */}
      <Timeline items={timelineItems} />

      {/* Pagination */}
      {total > 50 && (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
          <Pagination
            total={total}
            pageSize={50}
            onChange={(page) => {
              if (onPageChange) onPageChange(page);
            }}
            showSizeChanger={false}
            size="small"
          />
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline;
